import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { ToastService, AppNotification, Announcement, logger } from '@icc/shared/helpers/b2b';
import { dbVersion } from '@icc/b2b/shared';

@Injectable({
    providedIn: 'root',
})
export class AnnouncementService {
    socket!: Socket;
    machine = localStorage.getItem('id') || '';

    announcements: Announcement[] = [];
    announcements$ = new BehaviorSubject<Announcement[]>([]);

    constructor(private toastService: ToastService) {}

    start(userId: number, envDbVersion: number) {
        this.machine = localStorage.getItem('id+' + userId + '+' + dbVersion + '+' + envDbVersion) || '';
        if (!this.machine) {
            const clientId = this.makeClientId();
            localStorage.setItem('id+' + userId + '+' + dbVersion + '+' + envDbVersion, clientId);
            this.machine = clientId;
        }

        this.socket = io({
            path: '/api/socket.io',
            autoConnect: false,
            withCredentials: true,
            query: {
                announcements: 'true',
                token: '',
                machine: this.machine,
            },
            transports: ['websocket'],
        });
        this.socket.auth = {
            userId,
        };

        this.socket.connect();
        this.socket.on('newAnnouncements', async (announcements: Announcement[]) => {
            this.addAnnouncements(announcements);
        });
        this.socket.on('announcementClosed', async (id: number | number[]) => {
            if (Array.isArray(id)) {
                id.forEach(el => this.closedRemotely(el));
            } else {
                this.closedRemotely(id);
            }
        });
        this.socket.on('exception', (data: any) => {
            logger.log('exception', data);
        });
        return this.machine;
    }

    makeClientId() {
        return uuidv4().replace(/-/g, '').slice(-16);
    }

    addAnnouncements(announcements: Announcement[]) {
        const newNotifications = [];
        announcements.forEach(el => {
            const announcement = this.announcements.find(a => a.uuid === el.uuid);
            if (!announcement) {
                this.announcements.unshift(el);
                if (el.appNotification && !el.notificationClosed) {
                    newNotifications.push(this.toNotification(el));
                }
            } else if (announcement.pinned !== el.pinned) {
                announcement.pinned = el.pinned;
            }
        });
        this.announcements = this.announcements.filter(el => announcements.find(a => a.uuid === el.uuid));

        this.announcements$.next(this.announcements);
        this.toastService.addAll(newNotifications);
    }

    close(announcement: Announcement) {
        const index = this.announcements.findIndex(el => el.id === announcement.id);
        if (index > -1) {
            this.announcements.splice(index, 1);
            this.announcements$.next(this.announcements);
            this.socket.emit('announcementClosed', announcement.id);
        }
    }

    closedRemotely(id: number) {
        const index = this.announcements.findIndex(el => el.id === id);
        if (index > -1) {
            this.announcements.splice(index, 1);
            this.announcements$.next(this.announcements);
        }
    }

    notificationClosed(notification: AppNotification) {
        if (notification.id) {
            this.socket.emit('notificationClosed', notification.id);
        }
    }

    toNotification(announcement: Announcement): AppNotification {
        return {
           id: announcement.id,
           key: announcement.uuid,
           title: announcement.title,
           body: announcement.bodyShort,
           severity: announcement.severity,
           life: announcement.notificationLife,
           sticky: !announcement.notificationDisappearing,
           highlighted: announcement.highlighted,
           showDepartment: true,
           departmentName: announcement.departmentName,
        };
    }
}
